import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  AlertCircle,
  Loader2,
  Plus,
  Trash2,
  UserRound,
  Wifi,
  WifiOff
} from 'lucide-react';

import { useNetworkStatus } from '../../hooks';
import { useToast } from '../../hooks/useToast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../ui/utils';

const fieldClassName = cn(
  'flex h-10 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none',
  'placeholder:text-muted-foreground',
  'focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-ring/30',
  'disabled:cursor-not-allowed disabled:opacity-60'
);

const FormField = ({ type, id, value, onChange, placeholder, options, required, user }) => {
  let parsedOptions = [];
  if (options) {
    if (typeof options === 'string') {
      try {
        parsedOptions = JSON.parse(options);
      } catch (e) {
        console.error('Error parsing options:', e, options);
      }
    } else if (Array.isArray(options)) {
      parsedOptions = options;
    }
  }

  useEffect(() => {
    if (type === 'email' && user?.email && !value) {
      onChange({ target: { value: user.email } });
    }
  }, [type, user?.email, value, onChange]);

  switch (type) {
    case 'text':
      return (
        <Input
          id={id}
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className="h-10 rounded-lg"
        />
      );
    case 'textarea':
      return (
        <textarea
          id={id}
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          rows={4}
          className={cn(fieldClassName, 'min-h-[5.5rem] resize-y py-2.5')}
        />
      );
    case 'number':
      return (
        <Input
          id={id}
          type="number"
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className="h-10 rounded-lg"
        />
      );
    case 'email':
      return (
        <Input
          id={id}
          type="email"
          value={user?.email || value || ''}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={Boolean(user?.email)}
          className="h-10 rounded-lg"
        />
      );
    case 'date': {
      const dateValue = value ? new Date(value) : null;
      return (
        <div className="w-full candidate-datepicker">
          <DatePicker
            id={id}
            selected={dateValue}
            onChange={(date) => {
              onChange({
                target: {
                  value: date ? date.toISOString().split('T')[0] : ''
                }
              });
            }}
            dateFormat="dd/MM/yyyy"
            placeholderText={placeholder || 'DD/MM/YYYY'}
            required={required}
            customInput={
              <Input required={required} placeholder="DD/MM/YYYY" className="h-10 w-full rounded-lg" />
            }
            wrapperClassName="w-full"
          />
        </div>
      );
    }
    case 'select':
      return (
        <select
          id={id}
          value={value || ''}
          onChange={onChange}
          required={required}
          className={fieldClassName}
        >
          <option value="">{placeholder || 'Select an option'}</option>
          {parsedOptions?.map((option, i) => (
            <option key={`${id}-option-${i}`} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    case 'radio':
      return (
        <div className="flex flex-col gap-2.5 pt-1" role="radiogroup" aria-labelledby={id}>
          {parsedOptions?.map((option, i) => {
            const optionId = `${id}-option-${i}`;
            return (
              <label
                key={optionId}
                htmlFor={optionId}
                className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground"
              >
                <input
                  id={optionId}
                  type="radio"
                  name={id}
                  value={option}
                  checked={(value || '') === option}
                  onChange={() => onChange({ target: { value: option } })}
                  required={required && i === 0 && !value}
                  className="h-4 w-4 border-border text-primary focus:ring-primary/40"
                />
                <span>{option}</span>
              </label>
            );
          })}
        </div>
      );
    case 'checkbox':
      return (
        <div className="flex flex-col gap-2.5 pt-1">
          {parsedOptions?.map((option, i) => {
            const optionId = `${id}-option-${i}`;
            const checked = (value || []).includes(option);
            return (
              <label
                key={optionId}
                htmlFor={optionId}
                className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground"
              >
                <Checkbox
                  id={optionId}
                  checked={checked}
                  onCheckedChange={(next) => {
                    const currentValues = value || [];
                    const newValues = next
                      ? [...currentValues, option]
                      : currentValues.filter((v) => v !== option);
                    onChange({ target: { value: newValues } });
                  }}
                />
                <span>{option}</span>
              </label>
            );
          })}
        </div>
      );
    default:
      return (
        <Input
          id={id}
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className="h-10 rounded-lg"
        />
      );
  }
};

const CustomForm = ({ formData, user, onSubmit, isSubmitting: externalIsSubmitting }) => {
  const [formValues, setFormValues] = useState({});
  const [expandableSections, setExpandableSections] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const { isBrowserOnline } = useNetworkStatus();
  const isOnline = isBrowserOnline;
  const isFormSubmitting = isSubmitting || externalIsSubmitting;
  const [, setForceUpdate] = useState(0);

  useEffect(() => {
    if (!formData?.sections) return;

    const initialExpandableSections = {};
    formData.sections.forEach((section) => {
      if (section.expandable) {
        initialExpandableSections[section._id] = [{}];
      }
    });
    setExpandableSections(initialExpandableSections);
  }, [formData?.sections]);

  const getFieldKey = useCallback((sectionId, questionId, rowIndex = 0) => {
    const normalizedSectionId =
      typeof sectionId === 'object' && sectionId.$oid ? sectionId.$oid : String(sectionId);
    const normalizedQuestionId =
      typeof questionId === 'object' && questionId.$oid ? questionId.$oid : String(questionId);
    return `${normalizedSectionId}_${normalizedQuestionId}_${rowIndex}`;
  }, []);

  const shouldShowField = useCallback(
    (question, sectionId) => {
      if (!question.conditionalLogic) return true;

      const { conditions, conditionRelation } = question.conditionalLogic;
      if (!conditions || conditions.length === 0) return true;

      const results = conditions.map((condition) => {
        const { questionId: conditionQuestionId, operator, value } = condition;
        const questionId =
          typeof conditionQuestionId === 'object' && conditionQuestionId.$oid
            ? conditionQuestionId.$oid
            : String(conditionQuestionId);

        const dependentFieldKey = getFieldKey(sectionId, questionId);
        const dependentFieldValue = formValues[dependentFieldKey];
        const normalizedDependentValue =
          dependentFieldValue !== undefined ? String(dependentFieldValue) : '';
        const normalizedConditionValue = value !== undefined ? String(value) : '';

        switch (operator) {
          case 'equals':
            return normalizedDependentValue === normalizedConditionValue;
          case 'notEquals':
            return normalizedDependentValue !== normalizedConditionValue;
          case 'contains':
            return normalizedDependentValue.includes(normalizedConditionValue);
          case 'notContains':
            return !normalizedDependentValue.includes(normalizedConditionValue);
          case 'isEmpty':
            return !dependentFieldValue || dependentFieldValue === '';
          case 'isNotEmpty':
            return Boolean(dependentFieldValue && dependentFieldValue !== '');
          default:
            return true;
        }
      });

      if (conditionRelation === 'AND') {
        return results.every(Boolean);
      }
      return results.some(Boolean);
    },
    [formValues, getFieldKey]
  );

  const resetHiddenFieldValues = useCallback(() => {
    if (!formData?.sections) return;

    let valueChanged = false;
    const newFormValues = { ...formValues };
    const fieldsToEvaluate = [];

    formData.sections.forEach((section) => {
      if (!section.expandable) {
        section.questions.forEach((question) => {
          if (question.conditionalLogic) {
            const fieldKey = getFieldKey(section._id, question._id);
            fieldsToEvaluate.push({ question, section, fieldKey });
          }
        });
      }
    });

    fieldsToEvaluate.forEach(({ question, section, fieldKey }) => {
      const shouldShow = shouldShowField(question, section._id);
      if (!shouldShow && newFormValues[fieldKey] !== undefined && newFormValues[fieldKey] !== '') {
        newFormValues[fieldKey] = '';
        valueChanged = true;
      }
    });

    if (valueChanged) {
      setFormValues(newFormValues);
    }
  }, [formData, formValues, getFieldKey, shouldShowField]);

  useEffect(() => {
    const timerId = setTimeout(resetHiddenFieldValues, 50);
    return () => clearTimeout(timerId);
  }, [resetHiddenFieldValues]);

  const handleFieldChange = useCallback(
    (sectionId, questionId, e) => {
      const { value } = e.target;
      const fieldKey = getFieldKey(sectionId, questionId);

      setFormValues((prev) => {
        const newValues = {
          ...prev,
          [fieldKey]: value
        };
        setTimeout(() => setForceUpdate((count) => count + 1), 0);
        return newValues;
      });
    },
    [getFieldKey]
  );

  const handleTableFieldChange = useCallback(
    (sectionId, questionId, rowIndex, e) => {
      const { value } = e.target;
      const fieldKey = getFieldKey(sectionId, questionId, rowIndex);
      const normalizedQuestionId =
        typeof questionId === 'object' && questionId.$oid ? questionId.$oid : String(questionId);

      setFormValues((prev) => {
        const newValues = {
          ...prev,
          [fieldKey]: value
        };
        setTimeout(() => setForceUpdate((count) => count + 1), 0);
        return newValues;
      });

      setExpandableSections((prev) => {
        const sectionRows = [...(prev[sectionId] || [])];
        if (!sectionRows[rowIndex]) {
          sectionRows[rowIndex] = {};
        }
        sectionRows[rowIndex] = {
          ...sectionRows[rowIndex],
          [normalizedQuestionId]: value
        };
        return {
          ...prev,
          [sectionId]: sectionRows
        };
      });
    },
    [getFieldKey]
  );

  const addTableRow = (sectionId) => {
    setExpandableSections((prev) => ({
      ...prev,
      [sectionId]: [...(prev[sectionId] || []), {}]
    }));
  };

  const removeTableRow = (sectionId, rowIndex) => {
    setExpandableSections((prev) => {
      const sectionRows = [...(prev[sectionId] || [])];
      sectionRows.splice(rowIndex, 1);
      return {
        ...prev,
        [sectionId]: sectionRows
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!onSubmit) return;

    try {
      setIsSubmitting(true);
      await onSubmit(formValues, expandableSections);

      setFormValues({});
      const resetExpandableSections = {};
      formData.sections.forEach((section) => {
        if (section.expandable) {
          resetExpandableSections[section._id] = [{}];
        }
      });
      setExpandableSections(resetExpandableSections);

      toast({
        title: 'Form Submitted',
        description: 'Your form has been submitted successfully.',
        status: 'success',
        duration: 3000
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderRegularSection = (section) => {
    const questions = section.questions || [];
    const normalizedSectionId =
      typeof section._id === 'object' && section._id.$oid ? section._id.$oid : String(section._id);

    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 [grid-auto-flow:dense]">
        {questions.map((question) => {
          const isTextarea = question.type === 'textarea';
          const normalizedQuestionId =
            typeof question._id === 'object' && question._id.$oid
              ? question._id.$oid
              : String(question._id);

          if (!shouldShowField(question, section._id)) {
            return null;
          }

          return (
            <div
              key={`form-${normalizedSectionId}-${normalizedQuestionId}`}
              className={cn('min-w-0', isTextarea && 'md:row-span-2')}
            >
              <label
                htmlFor={`field-${normalizedSectionId}-${normalizedQuestionId}`}
                className="mb-1.5 block text-sm font-medium text-foreground"
              >
                {question.label}
                {question.required && <span className="ml-0.5 text-rose-500">*</span>}
              </label>
              <FormField
                type={question.type}
                id={`field-${normalizedSectionId}-${normalizedQuestionId}`}
                value={formValues[getFieldKey(section._id, question._id)]}
                onChange={(e) => handleFieldChange(section._id, question._id, e)}
                placeholder={question.placeholder}
                options={question.options}
                required={question.required}
                user={user}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderTableSection = (section) => {
    const rows = expandableSections[section._id] || [];
    const normalizedSectionId =
      typeof section._id === 'object' && section._id.$oid ? section._id.$oid : String(section._id);

    return (
      <div className="space-y-3">
        <div className="overflow-x-auto rounded-xl border border-border/70">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                {section.questions.map((question) => {
                  const normalizedQuestionId =
                    typeof question._id === 'object' && question._id.$oid
                      ? question._id.$oid
                      : String(question._id);
                  return (
                    <th
                      key={`th-${normalizedSectionId}-${normalizedQuestionId}`}
                      className="px-3 py-2.5 text-left text-xs font-semibold text-foreground"
                    >
                      {question.label}
                      {question.required && <span className="ml-0.5 text-rose-500">*</span>}
                    </th>
                  );
                })}
                <th className="w-12 px-2 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((rowData, rowIndex) => (
                <tr
                  key={`row-${normalizedSectionId}-${rowIndex}`}
                  className="border-b border-border/60 last:border-b-0"
                >
                  {section.questions.map((question) => {
                    const normalizedQuestionId =
                      typeof question._id === 'object' && question._id.$oid
                        ? question._id.$oid
                        : String(question._id);
                    return (
                      <td
                        key={`cell-${normalizedSectionId}-${normalizedQuestionId}-${rowIndex}`}
                        className="px-3 py-2 align-top"
                      >
                        <FormField
                          type={question.type}
                          id={`field-${normalizedSectionId}-${normalizedQuestionId}-${rowIndex}`}
                          value={
                            rowData?.[normalizedQuestionId] ||
                            formValues[getFieldKey(section._id, question._id, rowIndex)]
                          }
                          onChange={(e) =>
                            handleTableFieldChange(section._id, question._id, rowIndex, e)
                          }
                          placeholder={question.placeholder}
                          options={question.options}
                          required={question.required}
                        />
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 align-middle">
                    {rows.length > 1 && (
                      <button
                        type="button"
                        aria-label="Remove row"
                        onClick={() => removeTableRow(section._id, rowIndex)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-rose-600 transition-colors hover:bg-rose-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addTableRow(section._id)}
          className="gap-1.5"
        >
          <Plus className="h-4 w-4" />
          Add Row
        </Button>
      </div>
    );
  };

  if (!formData) {
    return (
      <div className="flex h-full items-center justify-center py-16 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading form...
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {isFormSubmitting && (
        <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-white/80 backdrop-blur-[1px]">
          <div className="flex flex-col items-center rounded-2xl border border-border bg-white px-8 py-6 shadow-lg">
            <Loader2 className="mb-3 h-8 w-8 animate-spin text-primary" />
            <p className="text-sm font-medium text-foreground">Submitting form...</p>
            <p className="mt-1 text-xs text-muted-foreground">Please wait while we process your details</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 border-b border-border/70 bg-white px-4 pb-4 pt-4 sm:px-6 sm:pb-5 sm:pt-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-primary/15 bg-primary/10 text-primary">
                <UserRound className="h-5 w-5" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-primary">
                  Candidate Information
                </p>
                <h2 className="truncate text-lg font-semibold text-foreground">
                  {formData.title || 'Your Details'}
                </h2>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
                  isOnline
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border-amber-200 bg-amber-50 text-amber-800'
                )}
              >
                {isOnline ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
                {isOnline ? 'Online' : 'Offline'}
              </span>
              {!isOnline && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Connect before submitting
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          <div className="space-y-4">
            {formData?.sections
              ?.slice()
              .sort((a, b) => a.order - b.order)
              .map((section) => (
                <section
                  key={`section-${section._id}`}
                  className="overflow-hidden rounded-2xl border border-border/60 bg-white shadow-sm"
                >
                  <div className="border-b border-border/60 bg-muted/30 px-4 py-3 sm:px-5">
                    <h3 className="text-sm font-semibold text-foreground">{section.title}</h3>
                    {section.description && (
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        {section.description}
                      </p>
                    )}
                  </div>
                  <div className="px-4 py-4 sm:px-5 sm:py-5">
                    {section.isTable ? renderTableSection(section) : renderRegularSection(section)}
                  </div>
                </section>
              ))}
          </div>
        </div>

        <div className="shrink-0 border-t border-border/70 bg-white px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={isFormSubmitting || !isOnline}
              className="min-w-[160px] font-semibold"
            >
              {isFormSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : !isOnline ? (
                <>
                  <WifiOff className="h-4 w-4" />
                  Offline
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </div>
        </div>
      </form>

      <style>{`
        .candidate-datepicker .react-datepicker-wrapper,
        .candidate-datepicker .react-datepicker__input-container {
          width: 100%;
        }
      `}</style>
    </div>
  );
};

export default CustomForm;
